import { apiClient } from './api-client';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'COACH' | 'STUDENT';
  licenseType: 'SEMI_PERSONALIZED' | 'PERSONALIZED' | 'NONE';
  isActive: boolean;
  createdAt: string;
}

export interface LoginResponse {
  accessToken: string;
  user: UserProfile;
}

export const authService = {
  /**
   * Intenta iniciar sesión con el correo y contraseña provistos
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const data = await apiClient.post<LoginResponse>('/api/auth/login', {
      email,
      password,
    });

    // Defensive check: if backend (for some reason) returns an inactive user,
    // do NOT persist the session client-side.
    if ((data.user as any).isActive === false) {
      throw new Error('El usuario está inactivo. Por favor contacta a un administrador.');
    }

    localStorage.setItem('discipline_token', data.accessToken);
    localStorage.setItem('discipline_user', JSON.stringify(data.user));

    // Disparar evento para que el router u otros componentes sepan del cambio
    window.dispatchEvent(new Event('auth_change'));

    return data;
  },

  /**
   * Cierra la sesión activa borrando el almacenamiento local
   */
  logout(): void {
    localStorage.removeItem('discipline_token');
    localStorage.removeItem('discipline_user');
    window.dispatchEvent(new Event('auth_change'));
    window.location.hash = '#/login';
  },

  /**
   * Obtiene los datos del usuario guardados en sesión
   */
  getUser(): UserProfile | null {
    const userStr = localStorage.getItem('discipline_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Obtiene el token JWT actual
   */
  getToken(): string | null {
    return localStorage.getItem('discipline_token');
  },

  /**
   * Comprueba si hay una sesión activa
   */
  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getUser();
  },

  /**
   * Valida si el usuario actual cuenta con alguno de los roles indicados
   */
  hasRole(allowedRoles: ('ADMIN' | 'COACH' | 'STUDENT')[]): boolean {
    const user = this.getUser();
    if (!user) return false;
    return allowedRoles.includes(user.role);
  },

  /**
   * Consulta al backend para verificar la validez del token y actualizar el perfil del usuario
   */
  async fetchMe(): Promise<UserProfile | null> {
    if (!this.getToken()) return null;
    try {
      const user = await apiClient.get<UserProfile>('/api/auth/me');
      localStorage.setItem('discipline_user', JSON.stringify(user));
      window.dispatchEvent(new Event('auth_change'));
      return user;
    } catch (err) {
      // El token ya no es válido, el interceptor ya habrá limpiado la sesión
      return null;
    }
  }
};
